import { CONFIG_STORAGE_KEY } from 'common/constants';
import { visualizationTypes } from 'components/constants';
import { getFormattedWidth } from 'components/utils';
import dedent from 'dedent';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getLocaleStorageConfigById, updateLocaleStorageHandler } from 'utils/localStorageHelpers';

interface HookParams {
  widgetId: string; 
  widthDefault: number;
  defaultItemValue?: DefaultItem | undefined;
  defaultVisualization?: VisualizationTypeDefault;
  defaultCategory?: DefaultCategories;
  defaultUnit?: DefaultUnits;
  defaultMetal?: DefaultMetals;
  defaultShowChange?: boolean;
  defaultTimeFrame?: DefaultTimeFrame;
  defaultNews?: DefaultNews;
  isDisplayShowChange?: boolean;
  defaultCurrency?: DefaultCurrency;
  defaultTicker?: DefaultTicker[];
  defaultCurrencyList?: DefaultCurrency[];
  defaultAllMetals?: DefaultMetals;
  defaultDaysNumber?: DefaultDaysNumber;
  defaultLocation?: DefaultLocation[];
  defaultListMetals?: DefaultMetalsList[];
}

export function useSettingsWidgetHandler({widgetId, widthDefault, defaultItemValue, defaultVisualization, defaultCategory, defaultUnit, defaultShowChange, isDisplayShowChange, defaultMetal, defaultTimeFrame, defaultNews, defaultCurrency, defaultTicker, defaultCurrencyList, defaultAllMetals, defaultDaysNumber, defaultLocation, defaultListMetals }: HookParams) {
  const [widthValue, setWidthValue] = useState(widthDefault);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [isContainerChecked, setIsContainerChecked] = useState(false);
  const [isTransparentChecked, setIsTransparentChecked] = useState(false);
  const [defaultItem, setDefaultItem] = useState<DefaultItem | undefined>(defaultItemValue);
  const [selectedVisualization, setSelectedVisualization] = useState<VisualizationTypeDefault | undefined>(defaultVisualization); 
  const [selectedCategory, setSelectedCategory] = useState<DefaultCategories | undefined>(defaultCategory);
  const [selectedMetal, setSelectedMetal] = useState<DefaultMetals | undefined>(defaultMetal);
  const [selectedTimeFrame, setSelectedTimeFrame] = useState<DefaultTimeFrame | undefined>(defaultTimeFrame);
  const [selectedCurrency, setSelectedCurrency] = useState<DefaultCurrency | undefined>(defaultCurrency);
  const [selectedTicker, setSelectedTicker] = useState<DefaultTicker[] | undefined>(defaultTicker);
  const [selectedCurrencyList, setSelectedCurrencyList] = useState<DefaultCurrency[] | undefined>(defaultCurrencyList);
  const [selectedAllMetals, setSelectedAllMetals] = useState<DefaultMetals | undefined>(defaultAllMetals);
  const [selectedNews, setSelectedNews] = useState<DefaultNews | undefined>(defaultNews);
  const [selectedUnit, setSelectedUnit] = useState<DefaultUnits | undefined>(defaultUnit);
  const [selectedDaysNumber, setSelectedDaysNumber] = useState<DefaultDaysNumber | undefined>(defaultDaysNumber); 
  const [selectedLocation, setSelectedLocation] = useState<DefaultLocation[] | undefined>(defaultLocation);
  const [selectedListMetals, setSelectedListMetals] = useState<DefaultMetalsList[] | undefined>(defaultListMetals); 
  const [showChange, setShowChange] = useState(defaultShowChange || false);
  const [isReset, setIsReset] = useState(false);
  const [formattedConfig, setFormattedConfig] = useState<string>('');
  const [config, setConfig] = useState<WidgetConfig>({
    widgetId: widgetId,
    width: widthDefault,
    isTransparent: false,
    colorTheme: 'light',
    useContainerSize: false,
    defaultItem: defaultItemValue,
    defaultView: defaultVisualization, 
    defaultCategory: defaultCategory, 
    defaultUnit: defaultUnit,
    defaultShowChange: defaultShowChange || false, 
    defaultMetal: defaultMetal,
    defaultTimeFrame: defaultTimeFrame,
    defaultNews: defaultNews,
    defaultCurrency: defaultCurrency,
    defaultTicker: defaultTicker,
    defaultCurrencyList: defaultCurrencyList,
    defaultAllMetals: defaultAllMetals,
    defaultDaysNumber: defaultDaysNumber,
    defaultLocation: defaultLocation,
    defaultListMetals: defaultListMetals,
  });

  const handleThemeChange = (e: any) => {
    console.log('theme', e.target.value);
    setTheme(e.target.value);
    if(e.target.value === 'dark') {
      setIsTransparentChecked(false);
    }
  };

  const updateFormattedConfig = () => {
    const configObject = {
      widgetId: widgetId,
      width: getFormattedWidth(config.useContainerSize, widthValue),
      isTransparent: isTransparentChecked,
      colorTheme: theme,
      ...(defaultItem ? { defaultItem: defaultItem } : {}), 
      ...(selectedVisualization ? { defaultView: selectedVisualization } : {}),
      ...(selectedCategory ? { defaultCategory: selectedCategory } : {}),
      ...(selectedUnit ? { defaultUnit: selectedUnit } : {}),
      ...(isDisplayShowChange ? { defaultShowChange: showChange } : {}),
      ...(selectedMetal ? { defaultMetal: selectedMetal } : {}),
      ...(selectedTimeFrame ? { defaultTimeFrame: selectedTimeFrame } : {}),
      ...(selectedNews ? { defaultNews: selectedNews } : {}),
      ...(selectedCurrency ? { defaultCurrency: selectedCurrency } : {}),
      ...(selectedTicker ? { defaultItemsList: selectedTicker } : {}),
      ...(selectedCurrencyList ? { defaultCurrencyList: selectedCurrencyList } : {}),
      ...(selectedAllMetals ? { defaultAllMetals: selectedAllMetals } : {}),
      ...(selectedDaysNumber ? { defaultDaysNumber: selectedDaysNumber } : {}),
      ...(selectedLocation ? { defaultLocation: selectedLocation } : {}),
      ...(selectedListMetals ? { defaultListMetals: selectedListMetals } : {}),
    };

    function formatValue(value:any) {
      if (Array.isArray(value)) {
        return `[${value.map(v => `'${v}'`).join(', ')}]`;
      }
      return JSON.stringify(value);
    }

    // Custom formatter for configObject
      const formattedConfigResult = dedent(`
        const config = {
${Object.entries(configObject)
                  .map(([key, value]) => {
                    const formatted = formatValue(value);
                    return `                              ${JSON.stringify(key)}: ${formatted},`;
                  })
                  .join('\n')}
      }`);
    
/*       const formattedConfigResult = dedent(`
        const config = ${
          JSON.stringify(configObject, null, 2)
            .split("\n")
            .map(line => line !== '{' ? "                                " + line : line) 
            .join("\n")
        }
    `);  */
    setFormattedConfig(formattedConfigResult);
  }

  const handleChange = (e: any) => {
    let inputValue = e.target.value;
    if (inputValue === '' || /^\d*$/.test(inputValue)) {
      setWidthValue(inputValue);
    }
  };

  const handleBlur = (limit:number) => {
    // Enforce minimum value of 300
    if (Number(widthValue) < limit) {
      setWidthValue(limit);
    }
  };

  const handleContainerChecked = (e: any) => {
/*     if (e.target.checked) {
      setWidthValue(1200);
    } else {
      setWidthValue(widthDefault); 
    } */
    setIsContainerChecked(e.target.checked);
  };

  const handleTrasparentChecked = (e: any) => {
    setIsTransparentChecked(e.target.checked);
  };

  const updateDefaultItem = (defaultItem: DefaultItem | undefined) => {
    setDefaultItem(defaultItem);
  };

  const handleApply = () => {
    const currentConfig = {
      width: widthValue,
      widgetId: widgetId,
      isTransparent: isTransparentChecked,
      colorTheme: theme,
      useContainerSize: isContainerChecked,
      defaultItem: defaultItem,
      defaultView: selectedVisualization,
      defaultCategory: selectedCategory,
      defaultUnit: selectedUnit, 
      defaultShowChange: showChange,
      defaultMetal: selectedMetal,
      defaultTimeFrame: selectedTimeFrame,
      defaultNews: selectedNews,
      defaultCurrency: selectedCurrency,
      defaultTicker: selectedTicker,
      defaultCurrencyList: selectedCurrencyList,
      defaultAllMetals: selectedAllMetals,
      defaultDaysNumber: selectedDaysNumber,
      defaultLocation: selectedLocation,
      defaultListMetals: selectedListMetals,
    }; 
    setConfig(currentConfig);
    updateLocaleStorageHandler(currentConfig, widgetId);
  }

  const handleResetSettings = () => {
    setWidthValue(widthDefault); 
    setTheme('light'); 
    setIsContainerChecked(false);
    setIsTransparentChecked(false); 
    setDefaultItem(undefined);
    setSelectedVisualization(defaultVisualization);
    setSelectedCategory(defaultCategory);
    setSelectedUnit(defaultUnit);
    setShowChange(defaultShowChange || false);
    setSelectedMetal(defaultMetal);
    setSelectedTimeFrame(defaultTimeFrame);
    setSelectedNews(defaultNews);
    setSelectedCurrency(defaultCurrency);
    setSelectedTicker(defaultTicker);
    setSelectedAllMetals(defaultAllMetals);
    setSelectedCurrencyList(defaultCurrencyList);
    setSelectedDaysNumber(defaultDaysNumber);
    setSelectedLocation(defaultLocation);
    setSelectedListMetals(defaultListMetals);
    const defaultConfigNew: WidgetConfig = {
      width: widthDefault,
      isTransparent: false,
      widgetId: widgetId,
      colorTheme: 'light',
      useContainerSize: false,
      defaultItem: defaultItemValue, 
      defaultView: defaultVisualization,
      defaultCategory: defaultCategory,
      defaultUnit: defaultUnit,
      defaultShowChange: defaultShowChange || false,
      defaultMetal: defaultMetal,
      defaultTimeFrame: defaultTimeFrame,
      defaultNews: defaultNews,
      defaultCurrency: defaultCurrency,
      defaultTicker: defaultTicker,
      defaultCurrencyList: defaultCurrencyList,
      defaultAllMetals: defaultAllMetals,
      defaultDaysNumber: defaultDaysNumber,
      defaultLocation: defaultLocation,
      defaultListMetals: defaultListMetals,
    }; 
    setConfig(defaultConfigNew); 
    updateLocaleStorageHandler(defaultConfigNew, widgetId);
    setIsReset(true);
    setTimeout(() => setIsReset(false), 1000);
  }

  useEffect(() => {
    updateFormattedConfig(); 
  }, [widthDefault, theme, isContainerChecked, isTransparentChecked, defaultItem, selectedVisualization, selectedCategory, selectedUnit, showChange, widthValue, selectedMetal, selectedTimeFrame, selectedNews, selectedCurrency, selectedTicker, selectedCurrencyList, selectedAllMetals, selectedDaysNumber, selectedLocation, selectedListMetals]); 

  useEffect(() => {
    const storageResult = getLocaleStorageConfigById(widgetId);
    console.log('storageResult', storageResult);
    if (storageResult) {
      setWidthValue(storageResult.width);
      setTheme(storageResult.colorTheme);
      setIsContainerChecked(storageResult.useContainerSize);
      setIsTransparentChecked(storageResult.isTransparent);
      setDefaultItem(storageResult.defaultItem);
      setSelectedVisualization(storageResult.defaultView);
      setSelectedCategory(storageResult.defaultCategory);
      setSelectedUnit(storageResult.defaultUnit);
      setShowChange(storageResult.defaultShowChange || false);
      setSelectedMetal(storageResult.defaultMetal);
      setSelectedTimeFrame(storageResult.defaultTimeFrame);
      setSelectedNews(storageResult.defaultNews);
      setSelectedCurrency(storageResult.defaultCurrency);
      setSelectedTicker(storageResult.defaultTicker); 
      setSelectedCurrencyList(storageResult.defaultCurrencyList);
      setSelectedAllMetals(storageResult.defaultAllMetals);
      setSelectedDaysNumber(storageResult.defaultDaysNumber);
      setSelectedLocation(storageResult.defaultLocation);
      setSelectedListMetals(storageResult.defaultListMetals);
      setConfig(storageResult);
    }
  }, [widgetId]); 

/*   useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);  */

  return {
    widthValue,
    theme,
    isContainerChecked,
    defaultItem,
    isTransparentChecked,
    handleThemeChange,
    handleChange,
    handleContainerChecked,
    handleTrasparentChecked,
    handleResetSettings, 
    handleApply,
    handleBlur, 
    updateDefaultItem,
    isReset,
    formattedConfig,
    selectedVisualization,
    setSelectedVisualization, 
    selectedCategory, 
    setSelectedCategory, 
    selectedUnit, 
    setSelectedUnit,
    selectedMetal,
    setSelectedMetal, 
    selectedTimeFrame, 
    setSelectedTimeFrame,
    selectedNews, 
    setSelectedNews,
    selectedCurrency, 
    setSelectedCurrency, 
    selectedTicker, 
    setSelectedTicker, 
    selectedCurrencyList, 
    setSelectedCurrencyList,
    selectedAllMetals, 
    setSelectedAllMetals,
    selectedDaysNumber,
    setSelectedDaysNumber,
    selectedLocation,
    setSelectedLocation,
    selectedListMetals, 
    setSelectedListMetals,
    showChange,
    setShowChange,
    config
  };
}
